import { parseISO, addBusinessDays, isAfter } from 'date-fns';
import {
  EstimatedShipDateResult,
  PROCESSING_TIMES,
  MINIMUM_PROCESSING_DAYS,
} from 'hooks/useEstimateKitShipDate/types';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import { EstimatedHandlingRequestData } from 'hooks/useGetDecoratedKitRequestLines/utils';
import _ from 'lodash';
import { KitRequestSchemaLite } from 'types/kitting';
import { getNowDate, formatToUniversalDate } from 'utils/dates';

/**
 * Finds the latest expected stock lot from a list of kit request lines
 */
export const findLatestExpectedStockLot = (
  decoratedKitRequestLines: DecoratedKitRequestLine[] | undefined
): EstimatedShipDateResult['latestExpectedStockLot'] => {
  if (!decoratedKitRequestLines?.length) return null;

  const expectedStockLots = decoratedKitRequestLines.flatMap((line) =>
    (line.stockLots ?? [])
      .filter((stockLot) => stockLot.pending === true)
      .map((stockLot) => ({
        stockLot,
        expectedBy: stockLot.expectedDate,
      }))
  );

  return expectedStockLots.length
    ? _.maxBy(expectedStockLots, 'expectedBy')
    : null;
};

/**
 * Calculates the estimated processing date based on handling request data
 */
export const calculateEstimatedProcessingDate = (
  decoratedKitRequestLines: DecoratedKitRequestLine[] | undefined,
  handlingRequestData: EstimatedHandlingRequestData,
  approvedAt?: string | null
): Date | null => {
  if (!decoratedKitRequestLines?.length) return null;

  const totalMinutes =
    handlingRequestData.numberOfReels * PROCESSING_TIMES.REEL +
    handlingRequestData.numberOfShipmentLines * PROCESSING_TIMES.SHIPMENT_LINE +
    handlingRequestData.numberOfMerges * PROCESSING_TIMES.MERGE +
    handlingRequestData.numberOfSplits * PROCESSING_TIMES.SPLIT;

  const totalDays = Math.max(
    Math.ceil(totalMinutes / (24 * 60)),
    MINIMUM_PROCESSING_DAYS
  );

  // Use approvedAt date as starting point if it exists, otherwise use current date
  const startDate = approvedAt ? parseISO(approvedAt) : getNowDate();

  return addBusinessDays(startDate, totalDays);
};

/**
 * Determines the final ship date based on processing date and expected stock lot
 *
 * If shipPartial is true, we can ship as soon as the processing is complete,
 * without waiting for expected stock lots since we don't need all items.
 *
 * If shipPartial is false, we need to wait for all expected stock lots to arrive
 * before shipping, so we use the later date between processing completion and
 * the latest expected stock lot arrival.
 */
export const calculateFinalShipDate = (
  decoratedKitRequestLines: DecoratedKitRequestLine[] | undefined,
  estimatedProcessingDate: Date | null,
  latestExpectedStockLot: EstimatedShipDateResult['latestExpectedStockLot'],
  shipPartial: boolean
): string | null => {
  if (!decoratedKitRequestLines?.length) return null;
  if (!estimatedProcessingDate) return null;

  // For partial shipments, we can ship as soon as processing is complete
  if (shipPartial) return formatToUniversalDate(estimatedProcessingDate);

  // For complete shipments, wait for expected stock if needed
  if (latestExpectedStockLot?.expectedBy) {
    const expectedDate = parseISO(latestExpectedStockLot.expectedBy);
    return isAfter(expectedDate, estimatedProcessingDate)
      ? formatToUniversalDate(expectedDate)
      : formatToUniversalDate(estimatedProcessingDate);
  }

  return formatToUniversalDate(estimatedProcessingDate);
};

export const getHasEstimatedShipDateChanged = ({
  kitRequest,
  estimatedShipDate,
}: {
  kitRequest: Pick<
    KitRequestSchemaLite,
    'approved' | 'estimatedShipDate' | 'targetShipDate'
  >;
  estimatedShipDate: EstimatedShipDateResult['estimatedShipDate'];
}) => {
  if (
    !kitRequest?.approved ||
    !kitRequest.estimatedShipDate ||
    !estimatedShipDate
  ) {
    return false;
  }

  return (
    estimatedShipDate > kitRequest.estimatedShipDate &&
    (!kitRequest.targetShipDate ||
      estimatedShipDate > kitRequest.targetShipDate)
  );
};

export const getEstimatedShipDateToRender = ({
  kitRequest,
  estimatedShipDate,
}: {
  kitRequest: Pick<
    KitRequestSchemaLite,
    'approved' | 'estimatedShipDate' | 'targetShipDate'
  >;
  estimatedShipDate: EstimatedShipDateResult['estimatedShipDate'];
}): string | null => {
  if (!kitRequest?.approved) {
    return estimatedShipDate;
  }

  if (estimatedShipDate) {
    const isLaterThanEstimated =
      kitRequest.estimatedShipDate &&
      parseISO(estimatedShipDate) > parseISO(kitRequest.estimatedShipDate);
    const isLaterThanTarget =
      kitRequest.targetShipDate &&
      parseISO(estimatedShipDate) > parseISO(kitRequest.targetShipDate);

    if (
      isLaterThanEstimated &&
      (!kitRequest.targetShipDate || isLaterThanTarget)
    ) {
      return estimatedShipDate;
    }
  }

  if (kitRequest.targetShipDate) {
    return kitRequest.targetShipDate;
  }

  return kitRequest.estimatedShipDate || null;
};
